import {createCoreRoute} from '@/stores/apolloPlatform/cores/portalNavRoute';
import {
  TreeNav,
  TreeNodeCore,
  TreeNodePage,
  makeCoreNavigationTree,
} from '@/stores/apolloPlatform/cores/portalNavigation';

export const useCoresStore = defineStore('cores', {
  state: () => ({
    navTree: undefined as TreeNav | undefined,
    removeNavHook: undefined as (() => void) | undefined,
  }),
  actions: {
    async setNavTree(pages: PaginatedSpherePage) {
      const navTree = makeCoreNavigationTree(pages.results);
      const coreRoutes = navTree.flatMap((coreNode) => createCoreRoute(coreNode));

      // Replace old core-dynamic with new
      if (this.router.hasRoute('core-dynamic')) this.router.removeRoute('core-dynamic');
      this.router.addRoute({name: 'core-dynamic', path: '', children: coreRoutes});

      this.navTree = reactive(navTree);

      const path = this.router.currentRoute.value.fullPath;
      const destRoute = this.router.resolve(path);

      if (destRoute.meta.node?.node_type === 'PageNode') this.expandNodes(destRoute.meta.node);
      await this.router.replace(destRoute);
      this.registerNavigationCallback(); // For future navigations
    },
    registerNavigationCallback() {
      if (this.removeNavHook) return; // Already registered
      this.removeNavHook = this.router.beforeEach((to) => {
        if (to.meta.node?.node_type === 'PageNode') this.expandNodes(to.meta.node);
      });
    },
    async readPages() {
      try {
        const {data} = await apiGetCmsPages();
        await this.setNavTree(data);
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    async readPage(detail_url: string, locale?: 'en' | 'nl') {
      try {
        const {data} = await apiGetCmsPage(detail_url, locale);
        return data;
      } catch (error) {
        throw apiErrorMessage(error);
      }
    },
    expandNodes(nodePage: TreeNodePage | undefined) {
      let node: TreeNodeCore | TreeNodePage | undefined = nodePage;
      while (node !== undefined) {
        if (node.node_type !== 'PageNode') break;
        node = node.parent;
        if (node?.children) reactive(node).expanded = true; // Don't collapse non-active nodes
      }
    },
    async switchLanguage() {
      /**
       * This action should be called when the user language has changed.
       * @see {@link stores/apolloPlatform/userSettings/userSettings.ts}
       * Re-fetch using the new Accept-Language header. This is done in the Axios request interceptor.
       * @see {@link api/apolloPlatform/apolloPlatformAxiosInstance.ts}
       */
      await this.readPages();
    },
  },
});
